import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { optimizedImage } from '../utils/productImage';

const byOrder = (a, b) => (a.order || 0) - (b.order || 0) || (a.name || '').localeCompare(b.name || '');
const LINE_H = 28; // px per link row (approx, incl. spacing)
const HEADER_COST = 1.8;
export const FLYOUT_H = 384; // same as hero banner (lg:h-96)
const PROMO_CARD_H = 122; // compact promo card height incl. margin
const PROMO_LABEL_H = 22;

// L2 bold headers + L3 links flowed into MANUAL columns. ALL L2 groups are always shown
// (per-group link budget shrinks to fit); a group may continue on the next column so the
// panel is filled edge to edge. Single-group case (e.g. "Altele – Pescuit"): the redundant
// header is skipped and its subcategories fill every column.
export const MegaMenuColumns = ({ category, categories, colCount = 4, heightPx, onNavigate, onHoverSub }) => {
  if (!category) return null;
  const groups = categories
    .filter((c) => c.parentId === category._id && c.isActive !== false)
    .sort(byOrder)
    .map((s2) => ({
      ...s2,
      children: categories.filter((c) => c.parentId === s2._id && c.isActive !== false).sort(byOrder),
    }));

  if (groups.length === 0) {
    return (
      <Link
        to={`/catalog?category=${category.slug}`}
        onClick={onNavigate}
        className="text-[15px] text-green-600 font-semibold hover:underline"
        data-testid={`megamenu-l1-direct-${category.slug}`}
      >
        Vezi toate produsele din {category.name}
      </Link>
    );
  }

  const linesPerCol = heightPx ? Math.max(6, Math.floor(heightPx / LINE_H)) : 14;
  const single = groups.length === 1 && groups[0].children.length > 0;

  // Regulă: un grup = o coloană. Fiecare grup arată cel mult câte linii încap într-o
  // coloană, iar ultima linie este „+X mai multe →”. Un grup NU curge în coloana următoare.
  const rows = [];
  const cols = Array.from({ length: colCount }, () => []);

  if (single) {
    // un singur grup (ex. „Altele – Pescuit”): copiii umplu toate coloanele
    const g = groups[0];
    const perCol = Math.max(1, linesPerCol);
    const capacity = perCol * colCount;
    const kids = g.children.slice(0, g.children.length > capacity ? capacity - 1 : capacity);
    kids.forEach((s3) => rows.push({ t: 'l', s3, g }));
    if (g.children.length > kids.length) rows.push({ t: 'm', g, extra: g.children.length - kids.length });
    rows.forEach((r, i) => cols[Math.min(colCount - 1, Math.floor(i / perCol))].push(r));
  } else {
    const cap = Math.max(3, linesPerCol - Math.ceil(HEADER_COST) - 1);
    const blocks = groups.map((g) => {
      const kids = g.children.slice(0, cap);
      const extra = g.children.length - kids.length;
      const blockRows = [{ t: 'h', g }, ...kids.map((s3) => ({ t: 'l', s3, g }))];
      if (extra > 0) blockRows.push({ t: 'm', g, extra });
      return { g, rows: blockRows, cost: blockRows.length - 1 + HEADER_COST };
    });

    let ci = 0;
    let used = 0;
    for (const b of blocks) {
      if (used > 0 && used + b.cost > linesPerCol && ci < colCount - 1) {
        ci++;
        used = 0;
      }
      if (used > 0 && used + b.cost > linesPerCol) {
        // ultima coloană e plină: păstrăm grupul vizibil doar cu titlul
        cols[ci].push(b.rows[0]);
        used += HEADER_COST;
        continue;
      }
      cols[ci].push(...b.rows);
      used += b.cost;
    }
  }

  const renderRow = (r, idx) => {
    if (r.t === 'h') {
      return (
        <Link
          key={`h-${r.g._id}`}
          to={`/catalog?category=${r.g.slug}`}
          onClick={onNavigate}
          onMouseEnter={onHoverSub ? () => onHoverSub(r.g) : undefined}
          className={`block font-bold text-[15px] text-gray-900 hover:text-green-600 mb-1 leading-snug border-b border-green-100 pb-1 ${idx > 0 ? 'mt-3' : ''}`}
          data-testid={`megamenu-l2-${r.g.slug}`}
        >
          {r.g.name}
        </Link>
      );
    }
    if (r.t === 'l') {
      return (
        <Link
          key={`l-${r.s3._id}`}
          to={`/catalog?category=${r.s3.slug}`}
          onClick={onNavigate}
          onMouseEnter={onHoverSub ? () => onHoverSub(r.s3) : undefined}
          className="block text-[14px] text-gray-600 hover:text-green-600 hover:translate-x-0.5 transition-transform py-[3px] leading-snug"
          data-testid={`megamenu-l3-${r.s3.slug}`}
        >
          {r.s3.name}
        </Link>
      );
    }
    return (
      <Link
        key={`m-${r.g._id}`}
        to={`/catalog?category=${r.g.slug}`}
        onClick={onNavigate}
        onMouseEnter={onHoverSub ? () => onHoverSub(r.g) : undefined}
        className="block text-[13px] font-medium text-green-600 hover:underline py-[3px]"
        data-testid={`megamenu-more-${r.g.slug}`}
      >
        +{r.extra} mai multe →
      </Link>
    );
  };

  return (
    <div className="flex gap-7">
      {cols.filter((c) => c.length).map((col, idx) => (
        <div key={idx} className="flex-1 min-w-0">
          {col.map(renderRow)}
        </div>
      ))}
    </div>
  );
};

// Floating flyout used by the sidebars (portal). R32 theme: rounded, green header,
// adaptive size, content fitted — NO scrollbar. Compact panels also show popular
// products (photo + price) in a right-hand column, scoped to the OPEN category and
// live-updated to the hovered SUBcategory inside the menu.
const promoCache = {};

const fetchPromo = (slug) => {
  if (promoCache[slug]) return Promise.resolve(promoCache[slug]);
  return productsAPI.getAll({ category: slug, limit: 4, sort_by: 'rating' })
    .then((res) => {
      const items = (res.data?.products || res.data || []).filter((p) => p && p.name).slice(0, 4);
      promoCache[slug] = items;
      return items;
    })
    .catch(() => []);
};

const MegaMenuPanel = ({ category, categories, anchor, cardTop, onEnter, onLeave }) => {
  const active = (c) => c.isActive !== false;
  const l2s = categories.filter((c) => c.parentId === category._id && active(c));

  const headerH = 60;
  const bodyPad = 40;
  const linesPerCol = Math.max(6, Math.floor((FLYOUT_H - headerH - bodyPad) / LINE_H));

  const kidsOf = (g) => categories.filter((x) => x.parentId === g._id && active(x)).length;
  const single = l2s.length === 1 && kidsOf(l2s[0]) > 0;
  // un grup = o coloană, cu maximum `cap` linkuri + rândul „+X mai multe”
  const cap = Math.max(3, linesPerCol - Math.ceil(HEADER_COST) - 1);
  let packedCols = 1;
  let colUsed = 0;
  let tallestCol = 0;
  if (single) {
    const total = kidsOf(l2s[0]);
    packedCols = Math.max(1, Math.min(4, Math.ceil(total / linesPerCol)));
    tallestCol = Math.min(linesPerCol, total);
  } else {
    for (const g of l2s) {
      const kids = Math.min(kidsOf(g), cap);
      const cost = HEADER_COST + kids + (kidsOf(g) > kids ? 1 : 0);
      if (colUsed > 0 && colUsed + cost > linesPerCol && packedCols < 4) {
        packedCols += 1;
        colUsed = 0;
      }
      colUsed += cost;
      tallestCol = Math.max(tallestCol, Math.min(colUsed, linesPerCol));
    }
  }
  const colsNeeded = packedCols;
  const showPromo = colsNeeded <= 2 && l2s.length > 0;

  // promoCat = the category/subcategory whose popular products are shown (hover-driven)
  const [promoCat, setPromoCat] = useState(category);
  const [promo, setPromo] = useState(promoCache[category.slug] || null);

  useEffect(() => {
    setPromoCat(category);
  }, [category.slug]);

  useEffect(() => {
    if (!showPromo) return undefined;
    let alive = true;
    const slug = promoCat.slug;
    if (promoCache[slug]) {
      setPromo(promoCache[slug]);
      return undefined;
    }
    setPromo(null); // never show a previous category's products while loading
    fetchPromo(slug).then((items) => {
      if (!alive) return;
      if (items.length > 0 || slug === category.slug) {
        setPromo(items);
      } else {
        // hovered subcategory has no products -> fall back to the open category's populars
        fetchPromo(category.slug).then((base) => { if (alive) setPromo(base); });
      }
    });
    return () => { alive = false; };
  }, [promoCat.slug, category.slug, showPromo]);

  if (l2s.length === 0 || !anchor) return null;

  const promoItems = showPromo && promo ? promo : [];
  const promoW = showPromo ? 236 : 0;
  const perCol = tallestCol;
  let height = Math.max(200, Math.min(FLYOUT_H, headerH + bodyPad + perCol * LINE_H + 8));
  // promo column: make room for at least 2 compact cards (capped at the flyout height)
  if (promoW) {
    height = Math.max(height, Math.min(FLYOUT_H, headerH + bodyPad + PROMO_LABEL_H + 2 * PROMO_CARD_H));
  }
  // how many promo cards actually FIT in the panel body — never render a cut-off card
  const promoFit = promoW
    ? Math.max(1, Math.floor((height - headerH - bodyPad - PROMO_LABEL_H + 8) / PROMO_CARD_H))
    : 0;
  const visiblePromo = promoItems.slice(0, promoFit);
  // panel bottom reaches the hovered row => straight horizontal mouse travel, no diagonal
  const top = Math.max(cardTop, Math.min(anchor.bottom + 4, window.innerHeight - 16) - height);
  const left = anchor.right + 2;
  const width = Math.min(Math.max(colsNeeded * 250 + 56 + promoW, 420 + promoW), window.innerWidth - left - 40);
  const colCount = colsNeeded;

  return createPortal(
    <div
      className="fixed bg-white border-2 border-green-100 rounded-2xl shadow-2xl overflow-hidden"
      style={{ left, top, width, height, zIndex: 9999 }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid="sidebar-category-flyout"
    >
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 flex items-center justify-between" style={{ height: headerH }}>
        <span className="font-bold text-lg truncate">{category.name}</span>
        <Link
          to={`/catalog?category=${category.slug}`}
          className="text-sm font-medium text-white/90 hover:text-white hover:underline flex-shrink-0 ml-4"
          data-testid="flyout-see-all"
        >
          Vezi toate →
        </Link>
      </div>
      <div className="px-6 py-5 overflow-hidden flex gap-5" style={{ height: height - headerH }}>
        <div className="flex-1 min-w-0">
          <MegaMenuColumns
            category={category}
            categories={categories}
            colCount={colCount}
            heightPx={height - headerH - bodyPad}
            onHoverSub={showPromo ? setPromoCat : undefined}
          />
        </div>
        {promoW > 0 && (
          <div className="w-[200px] flex-shrink-0 border-l border-green-100 pl-5" data-testid="flyout-promo-col">
            <p className="text-[11px] font-bold uppercase tracking-wide text-green-600 mb-1.5 truncate" data-testid="flyout-promo-label">
              Populare{promoCat.slug !== category.slug ? ` · ${promoCat.name}` : ''}
            </p>
            {visiblePromo.map((p) => (
              <Link
                key={p._id || p.id}
                to={`/product/${p._id || p.id}`}
                className="group flex flex-col mb-2 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-md transition-all p-1.5"
                data-testid={`flyout-promo-product-${p._id || p.id}`}
              >
                <img
                  src={optimizedImage(p.image, 200)}
                  onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.svg'; }}
                  alt={p.name}
                  className="w-full h-12 object-contain mb-1"
                  loading="lazy"
                />
                <span className="text-[12px] text-gray-700 group-hover:text-green-600 leading-tight line-clamp-2">{p.name}</span>
                <span className="text-[13px] font-bold text-green-600 mt-0.5">
                  {p.specialOrder
                    ? 'La cerere'
                    : `${Number(p.price || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2 })} lei`}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MegaMenuPanel;
